import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllVehicles } from '../../../lib/vehicleService';
import { Vehicle, formatPrice, formatMileage, proxyImage } from '../../../data/vehicles';

const TRUCK_BRANDS = ['Ford', 'Ram', 'GMC', 'Chevrolet', 'Nissan', 'Toyota'];

function isTruck(vehicle: Vehicle): boolean {
  const combined = `${vehicle.brand} ${vehicle.name} ${vehicle.type}`.toUpperCase();
  return (
    vehicle.type === 'Truck' ||
    /PICKUP|TRUCK|F-?150|F-?250|F-?350|SILVERADO|SIERRA|RANGER|TACOMA|TUNDRA|RAM\s*\d|TITAN|COLORADO|CANYON/.test(combined)
  );
}

export default function WorkTrucksSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [trucks, setTrucks] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  useEffect(() => {
    fetchAllVehicles()
      .then((data) => {
        const filtered = data.filter((v) => !v.sold && isTruck(v)).slice(0, 6);
        setTrucks(filtered);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (!loading && trucks.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #111008 50%, #0a0a0a 100%)' }}
    >
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/30 to-transparent"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#C9A84C]/3 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 sm:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <div className="flex items-center gap-3 sm:gap-4 mb-4">
              <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Work Ready
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Work Trucks
            </h2>
            <p className="text-white/60 max-w-xl text-xs sm:text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
              Built to haul, tow, and get the job done. Browse our selection of heavy-duty and light-duty trucks — Ford, Ram, GMC, Chevy and more. Nationwide delivery available.
            </p>
          </div>
          <Link
            to="/inventory?filter=trucks"
            className="inline-flex items-center gap-3 border border-[#C9A84C]/40 hover:border-[#C9A84C] text-[#C9A84C] px-6 py-3 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:bg-[#C9A84C]/10 self-start lg:self-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            View All Trucks
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Brand badges */}
        <div className={`flex flex-wrap gap-2 mb-10 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {TRUCK_BRANDS.map((brand) => (
            <Link
              key={brand}
              to="/inventory?filter=trucks"
              className="flex items-center gap-2 bg-white/5 hover:bg-[#C9A84C]/10 border border-white/10 hover:border-[#C9A84C]/40 px-3 py-1.5 rounded-sm text-[10px] text-white/60 hover:text-[#C9A84C] transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <i className="ri-truck-line text-xs"></i>
              {brand}
            </Link>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin"></div>
            <p className="text-white/40 text-xs tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Loading trucks...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {trucks.map((vehicle, index) => (
              <div
                key={vehicle.id}
                className={`group bg-[#141414] border border-white/5 hover:border-[#C9A84C]/40 rounded-sm overflow-hidden transition-all duration-700 hover:shadow-xl hover:shadow-[#C9A84C]/5 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <Link to="/inventory" className="block">
                  {/* Image */}
                  <div className="relative w-full h-48 sm:h-52 overflow-hidden">
                    <img
                      src={proxyImage(vehicle.image)}
                      alt={vehicle.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>

                    {/* Truck badge */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm border border-[#C9A84C]/30 px-2.5 py-1 rounded-sm">
                      <i className="ri-truck-line text-[#C9A84C] text-xs"></i>
                      <span className="text-[#C9A84C] text-[10px] font-bold tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Work Truck
                      </span>
                    </div>

                    {/* Condition badge */}
                    <div className="absolute top-3 right-3 bg-[#C9A84C] px-2.5 py-1 rounded-sm">
                      <span className="text-[#0d0d0d] text-[10px] font-bold tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {vehicle.condition.toUpperCase()}
                      </span>
                    </div>

                    {/* Price overlay */}
                    <div className="absolute bottom-3 left-3">
                      <p className="text-white text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.02em' }}>
                        {formatPrice(vehicle.price)}
                      </p>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <p className="text-[#C9A84C] text-[10px] tracking-widest uppercase mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {vehicle.brand} · {vehicle.year}
                    </p>
                    <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {vehicle.name}
                    </h3>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {[
                        { icon: 'ri-speed-line', label: formatMileage(vehicle.mileage) },
                        { icon: 'ri-settings-3-line', label: vehicle.transmission ?? 'Automatic' },
                        { icon: 'ri-gas-station-line', label: vehicle.fuel },
                      ].map((tag) => (
                        <div
                          key={tag.label}
                          className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-sm text-[10px] text-white/60"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          <i className={tag.icon}></i>
                          <span>{tag.label}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                      <span className="text-[10px] text-white/40 tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        View Details
                      </span>
                      <div className="w-9 h-9 flex items-center justify-center border border-[#C9A84C]/30 group-hover:bg-[#C9A84C] group-hover:border-[#C9A84C] rounded-sm transition-all">
                        <i className="ri-arrow-right-line text-[#C9A84C] group-hover:text-[#0d0d0d] transition-colors"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA banner */}
        <div className={`mt-12 sm:mt-16 relative overflow-hidden rounded-sm transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/858cfde07658936dd2320457201bd033.jpg"
              alt="Work Trucks"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
          </div>
          <div className="relative px-8 sm:px-12 py-10 sm:py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-truck-line text-[#C9A84C] text-lg"></i>
                <span className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Fleet &amp; Commercial
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Need a Fleet of Work Trucks?
              </h3>
              <p className="text-white/60 text-xs sm:text-sm max-w-lg" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}>
                We source multiple units for contractors, businesses, and fleet buyers. Get in touch for bulk pricing and nationwide delivery.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-6 sm:px-8 py-3 sm:py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
